import {
  useCreateNode,
  useDeleteNode,
  useStructuredWorkflow,
  useUpdateNode,
} from "@/api/workflow-hooks";
import { Button } from "@/components/button";
import { Switch } from "@/components/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import { CopyIcon, EnvelopeClosedIcon } from "@radix-ui/react-icons";
import {
  type DocumentNode,
  type EmailNode,
  isAPITriggerNode,
  isEmailNode,
} from "@repo/shared/workflow";
import { useStytchOrganization } from "@stytch/react/b2b";
import { produce } from "immer";
import { LinkIcon } from "lucide-react";
import { useMemo } from "react";

const Deployment: React.FC<{
  workflowId: string;
}> = ({ workflowId }) => {
  const {
    workflow,
    triggerNodes,
    triggerGroupNodeId,
    documentNodes,
    isLoading,
  } = useStructuredWorkflow(workflowId);

  const createNodeMut = useCreateNode(workflowId);
  const deleteNodeMut = useDeleteNode(workflowId);
  const updateNodeMut = useUpdateNode(workflowId);

  const { emailNode, apiNode } = useMemo(() => {
    const emailNode = triggerNodes?.find(isEmailNode);
    const apiNode = triggerNodes?.find(isAPITriggerNode);
    return { emailNode, apiNode };
  }, [triggerNodes]);

  return (
    <div className="flex py-8 w-full h-full justify-center">
      <div className="max-w-4xl w-full border rounded">
        <div className="border-b py-2 px-4 flex items-center justify-between sticky top-0 bg-secondary/50 z-10 h-14">
          <h3 className="font-medium py-2">Triggers</h3>
        </div>
        {!isLoading && (
          <Table>
            <TableHeader>
              <TableRow className="font-medium">
                <TableHead className="w-12" />
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-24">Enabled</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="border-b">
              <TableRow key={"email"} className="">
                <TableCell className="flex pt-3.5 items-center justify-center">
                  <EnvelopeClosedIcon className="h-4 w-4" />
                </TableCell>
                <TableCell className="align-top pt-3">Email</TableCell>
                <TableCell className="">
                  {emailNode && <EmailTriggerDescription node={emailNode} />}
                </TableCell>
                <TableCell className="align-top pt-2.5">
                  <Switch
                    checked={emailNode?.enabled ?? false}
                    onCheckedChange={(value) => {
                      if (!workflow) return;

                      if (emailNode) {
                        updateNodeMut.mutateAsync({
                          nodeId: emailNode.id,
                          update: produce(emailNode, (draft) => {
                            draft.enabled = value;
                          }),
                        });

                        return;
                      }

                      // Create node if it doesn't exist
                      createNodeMut.mutateAsync({
                        groupId: triggerGroupNodeId,
                        data: {
                          id: crypto.randomUUID(),
                          type: "entity",
                          subType: "email",
                          required: false,
                          acceptFrom: [],
                          replyAll: true,
                          name: "Email",
                          dependencyIds: [],
                          enabled: true,
                          toUsername: workflow.name
                            .toLocaleLowerCase()
                            .replaceAll(" ", "-"),
                        },
                      });
                    }}
                  />
                </TableCell>
              </TableRow>
              <TableRow key={"api"} className="">
                <TableCell className="flex pt-3.5 items-center justify-center">
                  <LinkIcon className="h-4 w-4" />
                </TableCell>
                <TableCell className="align-top pt-3">API</TableCell>
                <TableCell className="">
                  <APITriggerDescription
                    workflowId={workflowId}
                    documentNodes={documentNodes ?? []}
                  />
                </TableCell>
                <TableCell className="align-top pt-2.5">
                  <Switch
                    checked={apiNode?.enabled ?? false}
                    onCheckedChange={(value) => {
                      if (!workflow) return;

                      if (apiNode) {
                        updateNodeMut.mutateAsync({
                          nodeId: apiNode.id,
                          update: produce(apiNode, (draft) => {
                            draft.enabled = value;
                          }),
                        });

                        return;
                      }

                      // Create node if it doesn't exist
                      createNodeMut.mutateAsync({
                        groupId: triggerGroupNodeId,
                        data: {
                          name: "API",
                          dependencyIds: [],
                          id: crypto.randomUUID(),
                          type: "entity",
                          subType: "api-trigger",
                          enabled: true,
                        },
                      });
                    }}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

const EmailTriggerDescription: React.FC<{ node: EmailNode }> = ({ node }) => {
  const { organization } = useStytchOrganization();

  return (
    <div className="flex items-center -mt-0.5">
      {node.toUsername}@{organization?.organization_slug}.foundation.email
      <Button
        variant="ghost"
        size="icon"
        className="p-0 ml-2"
        onClick={() => {
          navigator.clipboard.writeText(
            `${node.toUsername}@${organization?.organization_slug}.foundation.email`,
          );
        }}
      >
        <CopyIcon />
      </Button>
    </div>
  );
};

const APITriggerDescription: React.FC<{
  workflowId: string;
  documentNodes: DocumentNode[];
}> = ({ workflowId, documentNodes }) => {
  const metadata = {
    workflow_id: workflowId,
    task_id: "Some unique task id",
    files: documentNodes.map((node) => ({
      model: node.name,
      name: `example-${node.name.toLowerCase().replaceAll(" ", "-")}.pdf`,
    })),
  };

  return (
    <div>
      <pre className="text-xs bg-secondary font-mono border rounded-md p-4">{`curl --location --request POST "https://api.tryfoundation.ai/v1/task" \

${metadata.files.map((file) => `  -F "files=@/Users/path-to-file/${file.name}; filename=${file.name}"`).join("\n")}
  -F "metadata='${JSON.stringify(metadata, null, 2)
    .split("\n")
    .map((line) => `  ${line}`)
    .join("\n")}'`}</pre>
    </div>
  );
};

export default Deployment;
